import { carouselNavigationItemSlotClassNames, ThemeInput } from "@fluentui/react-northstar";
import { getThemeForBarChart, getThemeForPieChart, getThemeMainColor } from "msTeamsUI/chartThemes/chartThemes";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { constants } from "shared/constants";
import ErrorOr from "shared/ErrorOr";
import pl from "shared/messages/pl";
import { CompanyReportGroupView } from "view360Card/components/companyReportGroupView/CompanyReportGroupView";
import { SearchEntityTypeEnum, V360ReportData } from "view360Card/models";
import FilterConfiguration from "view360Card/models/FilterConfiguration";
import view360Service from "view360Card/service";
import { V360ReportDataDto } from "view360Card/service/dtos/V360ReportDataDto";
import { MonthlyReportDataFromResponse } from "view360Card/store/MonthlyReportDataFromResponse";
import FailedToFetchReportError from "../error/FailedToFetchReportError";
import { SegmentsFilter, YearsFilter } from "../filters";
import { CompanyReportSectionConfig, IntermediaryNumber, Scope, SelectionTypeEnum } from "../models";
import { ReportSection } from "../ReportSection";

export interface SalesOverviewSectionProps {
    entityId: number;
    entityType: SearchEntityTypeEnum;
    intermediaryNumber: IntermediaryNumber | undefined;
    scope: Scope | undefined;
    isMobileView: boolean;
    sectionConfig: CompanyReportSectionConfig;
}

export const SalesOverviewSection: React.FC<SalesOverviewSectionProps> = (props) => {
    const [years, setYears] = useState(props.sectionConfig.periodDefinition.yearList);
    const [segments, setSegments] = useState(props.sectionConfig.company.segmentDefinition?.segmentList ?? []);

    const queryKey = [
        `overview_${props.sectionConfig.company.code}`,
        props.scope?.code,
        props.intermediaryNumber,
        years.filter((y) => y.selected).map((y) => y.code),
        segments.filter((s) => s.selected).map((s) => s.code),
    ];

    const { isFetching, isError, data } = useQuery(queryKey, async () => {
        let filterMonthly: FilterConfiguration | undefined;
        let filterCumulative: FilterConfiguration | undefined;
        let filterProductStructure: FilterConfiguration | undefined;
        let filterClaimRatio: FilterConfiguration | undefined;
        const intermediaryNumber = props.intermediaryNumber?.code ?? "";

        let modelMonthly: V360ReportData | undefined = undefined;
        let modelCumulative: V360ReportData | undefined = undefined;
        let modelProductStructure: V360ReportData | undefined = undefined;
        let modelClaimRatio: V360ReportData | undefined = undefined;

        const scope = props.scope?.code;
        const _segments = segments.filter((x) => x.selected).map((x) => x.code);
        const year = years.find((x) => x.selected)?.code ?? "";

        // TODO switch to multiquery endpoint
        const jobs: Promise<ErrorOr<V360ReportDataDto>>[] = [];

        if (
            props.sectionConfig.isReportAvailable([
                constants.v360ReportCodes.intermediary.salesOverviewSection.monthly,
                constants.v360ReportCodes.salesPerson.salesOverviewSection.monthly,
            ])
        ) {
            if (props.entityType === SearchEntityTypeEnum.Intermediary) {
                filterMonthly = FilterConfiguration.ForIntermediaryMonthly(
                    props.entityId,
                    props.sectionConfig.company.code,
                    scope,
                    _segments,
                    year
                );
            } else {
                filterMonthly = FilterConfiguration.ForSalesPersonMonthly(
                    props.entityId,
                    intermediaryNumber,
                    props.sectionConfig.company.code,
                    _segments,
                    year
                );
            }
            jobs.push(view360Service.loadReport(filterMonthly));
        }

        if (
            props.sectionConfig.isReportAvailable([
                constants.v360ReportCodes.intermediary.salesOverviewSection.cumulative,
                constants.v360ReportCodes.salesPerson.salesOverviewSection.cumulative,
            ])
        ) {
            if (props.entityType === SearchEntityTypeEnum.Intermediary) {
                filterCumulative = FilterConfiguration.ForIntermediaryCumulative(
                    props.entityId,
                    props.sectionConfig.company.code,
                    scope,
                    _segments,
                    year
                );
            } else {
                filterCumulative = FilterConfiguration.ForSalesPersonCumulative(
                    props.entityId,
                    intermediaryNumber,
                    props.sectionConfig.company.code,
                    _segments,
                    year
                );
            }
            jobs.push(view360Service.loadReport(filterCumulative));
        }

        if (
            props.sectionConfig.isReportAvailable([
                constants.v360ReportCodes.intermediary.salesOverviewSection.structure,
                constants.v360ReportCodes.salesPerson.salesOverviewSection.structure,
            ])
        ) {
            if (props.entityType === SearchEntityTypeEnum.Intermediary) {
                filterProductStructure = FilterConfiguration.ForIntermediaryProductStructure(
                    props.entityId,
                    props.sectionConfig.company.code,
                    scope,
                    _segments,
                    year
                );
            } else {
                filterProductStructure = FilterConfiguration.ForSalesPersonProductStructure(
                    props.entityId,
                    intermediaryNumber,
                    props.sectionConfig.company.code,
                    _segments,
                    year
                );
            }
            jobs.push(view360Service.loadReport(filterProductStructure));
        }

        if (
            props.sectionConfig.isReportAvailable([
                constants.v360ReportCodes.intermediary.salesOverviewSection.claimRatio,
            ])
        ) {
            if (props.entityType === SearchEntityTypeEnum.Intermediary) {
                filterClaimRatio = FilterConfiguration.ForIntermediaryClaimRatio(
                    props.entityId,
                    props.sectionConfig.company.code,
                    _segments
                );
                jobs.push(view360Service.loadReport(filterClaimRatio));
            }
        }

        const results = await Promise.all(jobs);

        const firstFailed = results.find((r) => r.isFailure);
        if (firstFailed) {
            throw new Error("Failed to fetch.");
        }

        const dtoMonthly = results.find(
            (r) =>
                r.data?.report?.code === constants.v360ReportCodes.intermediary.salesOverviewSection.monthly ||
                r.data?.report?.code === constants.v360ReportCodes.salesPerson.salesOverviewSection.monthly
        );
        const dtoCumulative = results.find(
            (r) =>
                r.data?.report?.code === constants.v360ReportCodes.intermediary.salesOverviewSection.cumulative ||
                r.data?.report?.code === constants.v360ReportCodes.salesPerson.salesOverviewSection.cumulative
        );
        const dtoProductStructure = results.find(
            (r) =>
                r.data?.report?.code === constants.v360ReportCodes.intermediary.salesOverviewSection.structure ||
                r.data?.report?.code === constants.v360ReportCodes.salesPerson.salesOverviewSection.structure
        );
        const dtoClaimRatio = results.find(
            (r) => r.data?.report?.code === constants.v360ReportCodes.intermediary.salesOverviewSection.claimRatio
        );

        modelMonthly = dtoMonthly?.data?.report
            ? new MonthlyReportDataFromResponse(dtoMonthly.data).toDomainModel()
            : undefined;
        modelCumulative = dtoCumulative?.data?.report
            ? new MonthlyReportDataFromResponse(dtoCumulative.data).toDomainModel()
            : undefined;
        modelProductStructure = dtoProductStructure?.data?.report
            ? new MonthlyReportDataFromResponse(dtoProductStructure.data).toDomainModel()
            : undefined;
        modelClaimRatio = dtoClaimRatio?.data?.report
            ? new MonthlyReportDataFromResponse(dtoClaimRatio.data!).toDomainModel()
            : undefined;
        return {
            monthly: modelMonthly,
            cumulative: modelCumulative,
            productStructure: modelProductStructure,
            claimRatio: modelClaimRatio,
        };
    });

    if (isError) {
        return (
            <FailedToFetchReportError
                message={pl.view360Card.reports.errors.failedToFetchOverviewReportsErrorMessage(
                    props.sectionConfig.company.name
                )}
            />
        );
    }

    const getFilters = () => {
        const result: JSX.Element[] = [];
        result.push(
            <YearsFilter
                key="years"
                years={years}
                onSelectedYearsChanged={(years) => setYears(years)}
                isMobileView={props.isMobileView}
                multiple={props.sectionConfig.periodDefinition.selectionType === SelectionTypeEnum.MULTIPLE}
                atLeastOneAlwaysSelected={props.sectionConfig.periodDefinition.isRequired}
            />
        );
        result.push(
            <SegmentsFilter
                key="segments"
                segmentList={segments}
                onSelectedSegmentsChanged={(segmentList) => setSegments(segmentList)}
                isMobileView={props.isMobileView}
                atLeastOneAlwaysSelected
                multiple={props.sectionConfig.company.segmentDefinition?.selectionType === SelectionTypeEnum.MULTIPLE}
                prefix={props.sectionConfig.company.name}
            />
        );
        return result;
    };

    const getContent = () => (
        <CompanyReportGroupView
            key={props.sectionConfig.company.code}
            monthly={data?.monthly}
            cumulative={data?.cumulative}
            productStructure={data?.productStructure}
            claimRatio={data?.claimRatio}
            pieTheme={getThemeForPieChart(props.sectionConfig.company.themeName)}
            barTheme={getThemeForBarChart(props.sectionConfig.company.themeName)}
            loading={isFetching}
            isMobileView={props.isMobileView}
        />
    );

    return (
        <ReportSection
            key={props.sectionConfig.company.code}
            isMobileView={props.isMobileView}
            title={props.sectionConfig.company.name}
            filters={getFilters()}
            reportContent={getContent()}
            highlightColor={getThemeMainColor(props.sectionConfig.company.themeName)}
        />
    );
};
